import { useEffect, useRef, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { penTurnersMenu, otherThingsMenu, pensAndPencilsMenu, primaryNav, site } from "@/data/site";
import { CtaLink } from "./cta";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";

export function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [penTurnersOpen, setPenTurnersOpen] = useState(false);
  const [pensOpen, setPensOpen] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const penTurnersRef = useRef<HTMLDivElement>(null);
  const pensRef = useRef<HTMLDivElement>(null);
  const otherRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setPenTurnersOpen(false);
    setPensOpen(false);
    setOtherOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        menuButtonRef.current?.focus({ preventScroll: true });
      }
      if (e.key === "Tab" && panelRef.current) {
        const nodes = panelRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    document.documentElement.classList.add("menu-lock");
    const focusFrame = requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLElement>("a,button")?.focus({ preventScroll: true });
    });
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKey);
      document.documentElement.classList.remove("menu-lock");
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!penTurnersOpen) return;
    const onDown = (e: MouseEvent) => {
      if (penTurnersRef.current && !penTurnersRef.current.contains(e.target as Node)) setPenTurnersOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setPenTurnersOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [penTurnersOpen]);

  useEffect(() => {
    if (!pensOpen) return;
    const onDown = (e: MouseEvent) => {
      if (pensRef.current && !pensRef.current.contains(e.target as Node)) setPensOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setPensOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pensOpen]);

  useEffect(() => {
    if (!otherOpen) return;
    const onDown = (e: MouseEvent) => {
      if (otherRef.current && !otherRef.current.contains(e.target as Node)) setOtherOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOtherOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [otherOpen]);

  return (
    <>
      <div className="relative z-50 hidden bg-ink text-canvas md:block">
        <div className="shell flex items-center justify-between py-2 text-[0.72rem] tracking-wide">
          <p className="text-canvas/75">{site.announcement}</p>
          <a
            href={`tel:${site.phoneHref}`}
            onClick={() => track("click_phone", { location: "announcement_bar" })}
            className="inline-flex items-center gap-2 text-canvas/85 hover:text-brass-glow motion-craft transition-colors"
          >
            <Phone aria-hidden="true" className="size-3.5" />
            {site.phoneDisplay}
          </a>
        </div>
      </div>

      <header className="sticky top-0 z-50 bg-canvas shadow-[0_1px_0_var(--color-hairline)]">
        <div className="shell flex items-center justify-between gap-6 py-3.5">
          <Link to="/" className="group flex min-h-11 shrink-0 flex-col justify-center leading-none" aria-label={`${site.name} — home`}>
            <span className="block font-[family-name:var(--font-mark)] text-[1.28rem] leading-none tracking-tight text-ink whitespace-nowrap">
              Paul&apos;s Pens <span className="italic text-walnut">n</span> Things
            </span>
            <span className="mt-1 hidden text-[0.6rem] tracking-[0.22em] uppercase text-muted-foreground sm:block">
              Handcrafted · Paragould, Arkansas
            </span>
          </Link>

          <nav aria-label="Main" className="hidden items-center gap-1 lg:flex">
            <HeaderLink to="/" label="Home" />
            <div
              ref={pensRef}
              className="relative"
              onMouseEnter={() => setPensOpen(true)}
              onMouseLeave={() => setPensOpen(false)}
            >
              <button
                type="button"
                aria-expanded={pensOpen}
                aria-controls="pens-menu"
                aria-haspopup="true"
                onMouseEnter={() => setPensOpen(true)}
                onClick={() => setPensOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-sm px-3 py-2 text-sm font-medium text-ink/80 hover:text-ink motion-craft transition-colors data-[status=active]:text-ink data-[status=active]:underline data-[status=active]:decoration-brass data-[status=active]:underline-offset-8"
              >
                Pens and Pencils
                <ChevronDown
                  aria-hidden="true"
                  className={cn("size-3.5 motion-craft transition-transform", pensOpen && "rotate-180")}
                />
              </button>
              {pensOpen && (
                <div
                  id="pens-menu"
                  onMouseEnter={() => setPensOpen(true)}
                  onMouseLeave={() => setPensOpen(false)}
                  className="absolute left-1/2 top-full z-50 w-[min(20rem,80vw)] -translate-x-1/2 pt-2"
                >
                  <ul className="grid gap-0.5 rounded-sm border border-hairline bg-popover p-2 shadow-[var(--shadow-raise)]">
                    {pensAndPencilsMenu.map((item) => (
                      <li key={item.label}>
                        <Link
                          to={item.to}
                          onClick={() => setPensOpen(false)}
                          className="block rounded-sm px-3 py-2 text-sm font-medium text-ink hover:bg-paper motion-craft transition-colors"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            </div>
            <div
              ref={otherRef}
              className="relative"
              onMouseEnter={() => setOtherOpen(true)}
              onMouseLeave={() => setOtherOpen(false)}
            >
              <button
                type="button"
                aria-expanded={otherOpen}
                aria-controls="other-things-menu"
                aria-haspopup="true"
                onMouseEnter={() => setOtherOpen(true)}
                onClick={() => setOtherOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-sm px-3 py-2 text-sm font-medium text-ink/80 hover:text-ink motion-craft transition-colors"
              >
                Other Things
                <ChevronDown
                  aria-hidden="true"
                  className={cn("size-3.5 motion-craft transition-transform", otherOpen && "rotate-180")}
                />
              </button>
              {otherOpen && (
                <div
                  id="other-things-menu"
                  onMouseEnter={() => setOtherOpen(true)}
                  onMouseLeave={() => setOtherOpen(false)}
                  className="absolute left-1/2 top-full z-50 w-[min(20rem,80vw)] -translate-x-1/2 pt-2"
                >
                  <ul className="grid gap-0.5 rounded-sm border border-hairline bg-popover p-2 shadow-[var(--shadow-raise)]">
                    {otherThingsMenu.map((item) => (
                      <li key={item.label}>
                        <Link
                          to={item.to}
                          onClick={() => setOtherOpen(false)}
                          className="block rounded-sm px-3 py-2 text-sm font-medium text-ink hover:bg-paper motion-craft transition-colors"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {primaryNav
              .filter((l) => l.to !== "/" && l.to !== "/custom-pens" && l.to !== "/other-creations")
              .map((l) => (
                <HeaderLink key={l.to} to={l.to} label={l.label} />
              ))}
            <div ref={penTurnersRef} className="relative">
              <button
                type="button"
                aria-expanded={penTurnersOpen}
                aria-controls="pen-turners-menu"
                aria-haspopup="true"
                onClick={() => setPenTurnersOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-sm px-3 py-2 text-sm font-medium text-ink/80 hover:text-ink motion-craft transition-colors"
              >
                For Penturners
                <ChevronDown
                  aria-hidden="true"
                  className={cn("size-3.5 motion-craft transition-transform", penTurnersOpen && "rotate-180")}
                />
              </button>
              {penTurnersOpen && (
                <div
                  id="pen-turners-menu"
                  className="absolute left-1/2 top-full z-50 mt-2 w-[min(34rem,80vw)] -translate-x-1/2 rounded-sm border border-hairline bg-popover p-2 shadow-[var(--shadow-raise)]"
                >
                  <ul className="grid gap-1 sm:grid-cols-2">
                    {penTurnersMenu.map((item) => (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          className="block rounded-sm px-3 py-2.5 hover:bg-paper motion-craft transition-colors"
                        >
                          <span className="block text-sm font-semibold text-ink">{item.label}</span>
                          <span className="mt-0.5 block text-xs text-muted-foreground">{item.description}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center gap-2">
            <CtaLink to="/design-your-piece" variant="primary" className="hidden h-11 text-[0.8rem] sm:inline-flex">
              Design Your Piece
            </CtaLink>
            <a
              href={`tel:${site.phoneHref}`}
              onClick={() => track("click_phone", { location: "header_mobile" })}
              aria-label={`Call Paul at ${site.phoneDisplay}`}
              className="tap-safe inline-flex items-center justify-center rounded-sm border border-ink/20 text-ink sm:hidden"
            >
              <Phone aria-hidden="true" className="size-5" />
            </a>
            <button
              ref={menuButtonRef}
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              aria-haspopup="dialog"
              aria-label="Open menu"
              className="tap-safe inline-flex items-center justify-center rounded-sm border border-ink/20 lg:hidden"
            >
              <Menu aria-hidden="true" className="size-5" />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div
          id="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
          className="fixed inset-0 z-[60] bg-canvas lg:hidden"
        >
          <div ref={panelRef} className="flex h-dvh flex-col overflow-y-auto">
            <div className="shell flex items-center justify-between py-4">
              <span className="font-[family-name:var(--font-display)] text-lg">Menu</span>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  menuButtonRef.current?.focus({ preventScroll: true });
                }}
                aria-label="Close menu"
                className="tap-safe inline-flex items-center justify-center rounded-sm border border-ink/20"
              >
                <X aria-hidden="true" className="size-5" />
              </button>
            </div>
            <nav aria-label="Mobile" className="shell flex-1 pb-8">
              <ul className="divide-y divide-hairline border-y border-hairline">
                {primaryNav
                  .filter((l) => l.to !== "/custom-pens" && l.to !== "/other-creations")
                  .map((l) => (
                    <li key={l.to}>
                      <Link to={l.to} className="flex min-h-14 items-center text-lg text-ink">
                        {l.label}
                      </Link>
                    </li>
                  ))}
              </ul>
              <p className="eyebrow mt-8">Pens and Pencils</p>
              <ul className="mt-2 divide-y divide-hairline border-y border-hairline">
                {pensAndPencilsMenu.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="flex min-h-12 items-center text-base text-ink">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="eyebrow mt-8">Other Things</p>
              <ul className="mt-2 divide-y divide-hairline border-y border-hairline">
                {otherThingsMenu.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="flex min-h-12 items-center text-base text-ink">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="eyebrow mt-8">For Penturners</p>
              <ul className="mt-2 divide-y divide-hairline border-y border-hairline">
                {penTurnersMenu.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="flex min-h-14 items-center text-base text-ink">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-8 space-y-3">
                <CtaLink to="/design-your-piece" className="h-12 w-full">
                  Design Your Piece
                </CtaLink>
                <a
                  href={`tel:${site.phoneHref}`}
                  onClick={() => track("click_phone", { location: "mobile_menu" })}
                  className="tap-safe flex items-center justify-center gap-2 rounded-sm border border-ink/20 text-sm font-semibold"
                >
                  <Phone aria-hidden="true" className="size-4" /> {site.phoneDisplay}
                </a>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}

function HeaderLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      activeOptions={{ exact: to === "/" }}
      className="rounded-sm px-3 py-2 text-sm font-medium text-ink/80 hover:text-ink motion-craft transition-colors data-[status=active]:text-ink data-[status=active]:underline data-[status=active]:decoration-brass data-[status=active]:underline-offset-8"
    >
      {label}
    </Link>
  );
}
